import { isPublishedStatus, isStoppedStatus, isUnsettledStatus } from './status';

/**
 * What a render already did on one account, for the line under the account
 * on the compose page: how many posts reached the platform, how many are
 * still on their way (`isUnsettledStatus`), and how many did not go out
 * (`isStoppedStatus`: failed, waiting on the user, or cancelled). A status
 * newer than this client counts in none.
 */
export type PriorPostCounts = { published: number; pending: number; stopped: number };

export const countPriorPosts = (posts: readonly { status: string }[]): PriorPostCounts => {
  const counts: PriorPostCounts = { published: 0, pending: 0, stopped: 0 };
  for (const { status } of posts) {
    if (isPublishedStatus(status)) counts.published += 1;
    else if (isUnsettledStatus(status)) counts.pending += 1;
    else if (isStoppedStatus(status)) counts.stopped += 1;
  }
  return counts;
};

const times = (n: number): string => (n === 1 ? 'once' : `${n} times`);

/**
 * The line itself, or null when there is nothing to say (the common case: a
 * fresh render). The first thing worth saying leads; the rest follow as
 * clauses: "Posted once before · 2 more scheduled · 1 didn’t go out".
 */
export const describePriorPosts = ({
  published,
  pending,
  stopped,
}: PriorPostCounts): string | null => {
  const clauses: string[] = [];
  if (published > 0) clauses.push(`Posted ${times(published)} before`);
  if (pending > 0) {
    clauses.push(
      published > 0 ? `${pending} more scheduled` : `Scheduled ${times(pending)} already`,
    );
  }
  if (stopped > 0) {
    clauses.push(
      clauses.length > 0
        ? `${stopped} didn’t go out`
        : `${stopped} earlier ${stopped === 1 ? 'post' : 'posts'} didn’t go out`,
    );
  }
  return clauses.length > 0 ? clauses.join(' · ') : null;
};
